import type React from 'react';
import { toast } from 'react-toastify';
import { useEntityStore } from '@ContractBuilder/store/entity.store';
import { useSchemaStore } from '@ContractBuilder/store/schema.store';
import type { FormError } from '@root/@types/types';
import { normaliseDataPathIndices } from '@root/helpers/schema/getConditionalDriverFields';
import { shakeOffUnusedProperties } from '@root/helpers/schema/shakeOffUnusedProperties';
import { isFormViewPath } from '@root/src/utils/app-paths';
import { cloneDeep, debounce, get as lodashGet, isEqual, set } from 'lodash-es';
import { createWithEqualityFn } from 'zustand/traditional';

import { applyAutoPopulateRules } from './auto-populate-from-schema';

const SCHEMA_REFRESH_DEBOUNCE_MS = 150;

const refreshSchemaDebounced = debounce(
  (nextFormValues: Record<string, any>) => {
    useSchemaStore.getState().refreshResolvedSchema(nextFormValues);
  },
  SCHEMA_REFRESH_DEBOUNCE_MS,
  { leading: false, trailing: true },
);

export interface DatapointsStoreState {
  formErrors: FormError[];
  formValues: Record<string, any>;
  isDirty: boolean;
  isSubmitting: boolean;
  onChange: (...args: any) => void;
  onFormReset: () => void;
  onSubmit: () => Promise<void>;
  reset: () => void;
  setFormErrors: React.Dispatch<React.SetStateAction<FormError[]>>;
  setFormValues: (
    param: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>),
    options?: { immediate?: boolean },
  ) => void;
  clearUnusedData: () => void;
  removedUnusedData: string[];
  addedSectionAtIndex?: number;
  removedSectionAtIndex?: number;
  setAddedSectionAtIndex: (addedSectionAtIndex?: number) => void;
  setRemovedSectionAtIndex: (removedSectionAtIndex?: number) => void;
}

export const useDatapointsStore = createWithEqualityFn<DatapointsStoreState>(
  (setter, get) => ({
    formErrors: [],
    formValues: {},
    isDirty: false,
    isSubmitting: false,
    removedUnusedData: [],
    onChange: (...args) => {
      const nextFormValues = cloneDeep(get().formValues);
      let changedPath: string | undefined;

      if (args.length === 1) {
        const [event] = args;
        changedPath = event.target.name;
        set(nextFormValues, event.target.name, event.target.value);
      }

      if (args.length === 2) {
        const [value, name] = args;
        changedPath = name;
        set(nextFormValues, name, value);
      }

      if (changedPath) {
        const autoPopulateRules = useSchemaStore.getState().autoPopulateRules;
        applyAutoPopulateRules(autoPopulateRules, nextFormValues, changedPath);
      }

      const driverFields = useSchemaStore.getState().conditionalDriverFields;
      const immediate = changedPath ? driverFields.has(normaliseDataPathIndices(changedPath)) : true;

      get().setFormValues(nextFormValues, { immediate });
    },
    onFormReset: () => {
      const initialState = cloneDeep(useEntityStore.getState().submission?.data_items ?? {});
      refreshSchemaDebounced.cancel();
      useSchemaStore.getState().cancelValidation();
      useSchemaStore.getState().refreshResolvedSchema(initialState);
      setter({ formValues: initialState, isDirty: false, isSubmitting: false, removedUnusedData: [] });
    },
    onSubmit: async () => {
      toast.dismiss();
      const updateFormData = useEntityStore.getState().updateFormData;
      setter({ isSubmitting: true });

      refreshSchemaDebounced.flush();
      useSchemaStore.getState().flushValidation();
      get().clearUnusedData();

      try {
        const formValues = get().formValues;
        const result = await updateFormData(formValues);

        if (result?.message === 'success' && isFormViewPath()) {
          toast.success('Data saved successfully');
        }
      } catch (error) {
        get().setFormErrors(error as any);
        setter({ removedUnusedData: [] });
      } finally {
        setter({ isSubmitting: false, isDirty: false });
      }
    },
    reset: () => {
      refreshSchemaDebounced.cancel();
      useSchemaStore.getState().cancelValidation();
      setter({ formErrors: [], formValues: {}, isDirty: false, isSubmitting: false, removedUnusedData: [] });
    },
    setFormErrors: (param) => {
      if (typeof param === 'function') {
        return setter({ formErrors: param(get().formErrors) });
      }

      return setter({ formErrors: param });
    },
    setFormValues: (param, options) => {
      const oldFormValues = useEntityStore.getState().submission?.data_items ?? {};
      const nextFormValues = typeof param === 'function' ? param(get().formValues) : param;

      if (options?.immediate) {
        refreshSchemaDebounced.cancel();
        useSchemaStore.getState().refreshResolvedSchema(nextFormValues);
      } else {
        refreshSchemaDebounced(nextFormValues);
      }

      setter({
        formValues: nextFormValues,
        isDirty: !isEqual(nextFormValues, oldFormValues),
      });

      if (options?.immediate) {
        useSchemaStore.getState().cancelValidation();
        useSchemaStore.getState().validate();
      } else {
        useSchemaStore.getState().triggerValidation();
      }
    },
    setAddedSectionAtIndex: (addedSectionAtIndex?: number) => {
      const cloned = cloneDeep(get().formValues);
      get().formValues?.risk_details?.split_by_sections?.forEach((splitBySectionsItem: string) => {
        const value = lodashGet(cloned, splitBySectionsItem);
        const newItems = [...value.slice(0, addedSectionAtIndex), {}, ...value.slice(addedSectionAtIndex)];
        set(cloned, splitBySectionsItem, newItems);
      });
      get().setFormValues(cloned, { immediate: true });
      setter({
        addedSectionAtIndex,
        removedSectionAtIndex: undefined,
      });
    },
    setRemovedSectionAtIndex: (removedSectionAtIndex?: number) => {
      const cloned = cloneDeep(get().formValues);
      get().formValues?.risk_details?.split_by_sections?.forEach((splitBySectionsItem: string) => {
        const value = lodashGet(cloned, splitBySectionsItem);
        const newItems = [...value.slice(0, removedSectionAtIndex), ...value.slice(removedSectionAtIndex! + 1)];
        set(cloned, splitBySectionsItem, newItems);
      });
      get().setFormValues(cloned, { immediate: true });
      setter({
        removedSectionAtIndex,
        addedSectionAtIndex: undefined,
      });
    },
    clearUnusedData: () => {
      const schema = useSchemaStore.getState().schema;
      const formValues = get().formValues;

      const removedPaths: string[] = [];
      const prunedFormValues = schema
        ? shakeOffUnusedProperties(schema, formValues, {
            onPathRemoved: (path) => removedPaths.push(path),
          })
        : formValues;

      get().setFormValues(prunedFormValues, { immediate: true });
      setter({ removedUnusedData: removedPaths });
    },
  }),
  isEqual,
);
